// Life & Wealth Solutions — Scraper Leads Review Queue
// PRA INTERNAL — john@phoenixrescueapps.online, teresa@, shawn@ only

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const PRA_EMAILS = new Set([
  'john@phoenixrescueapps.online',
  'teresa@lifeandwealthsolutions.online',
  'shawn@lifeandwealthsolutions.online'
]);

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface ScraperLead {
  id: string;
  company_name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  state: string;
  address: string | null;
  industry: string | null;
  policy_types: string | null;
  estimated_premium: number;
  is_whale: boolean;
  lead_score: number;
  email_status: string;
  source: string;
  queue_status: string;
  scraped_at: string;
}

const ScraperLeadsQueue: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [leads, setLeads]       = useState<ScraperLead[]>([]);
  const [filter, setFilter]     = useState<'pending'|'approved'|'rejected'|'all'>('pending');
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [total, setTotal]       = useState(0);
  const [toast, setToast]       = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const [authorized, setAuthorized] = useState(false);

  const token   = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Authorization check — runs as effect, NOT as early return before hooks
  useEffect(() => {
    if (user?.email && PRA_EMAILS.has(user.email)) {
      setAuthorized(true);
    }
  }, [user]);

  const fetchLeads = useCallback(async () => {
    if (!user?.email || !PRA_EMAILS.has(user.email)) return;
    setLoading(true);
    try {
      const res  = await fetch(`${API}/scraper-leads/queue?status_filter=${filter}&limit=100`, { headers });
      const data = await res.json();
      setLeads(data.leads || []);
      setTotal(data.total || 0);
    } catch { setToast('Failed to load queue'); }
    finally { setLoading(false); }
  }, [filter, user]);

  useEffect(() => {
    if (!authorized) return;
    fetchLeads();
  }, [authorized, fetchLeads]);

  // Access guard rendered AFTER all hooks
  if (!user?.email || !PRA_EMAILS.has(user.email)) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-900">Access Restricted</p>
          <p className="text-sm text-gray-500 mt-2">Phoenix Rescue Apps internal only.</p>
          <button onClick={() => navigate('/')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const approveLead = async (id: string) => {
    setProcessing(id);
    try {
      await fetch(`${API}/scraper-leads/${id}/approve`, { method: 'POST', headers });
      setLeads(p => p.filter(l => l.id !== id));
      setTotal(t => t - 1);
      showToast('✓ Lead approved and added to pipeline');
    } catch { showToast('Failed to approve'); }
    finally { setProcessing(null); }
  };

  const rejectLead = async (id: string) => {
    setProcessing(id);
    try {
      await fetch(`${API}/scraper-leads/${id}/reject`, { method: 'POST', headers });
      setLeads(p => p.filter(l => l.id !== id));
      setTotal(t => t - 1);
      showToast('Lead rejected');
    } catch { showToast('Failed to reject'); }
    finally { setProcessing(null); }
  };

  const bulkApprove = async () => {
    if (!selected.size) return;
    try {
      await fetch(`${API}/scraper-leads/bulk-approve`, {
        method: 'POST', headers,
        body: JSON.stringify(Array.from(selected))
      });
      setLeads(p => p.filter(l => !selected.has(l.id)));
      setTotal(t => t - selected.size);
      showToast(`✓ ${selected.size} leads approved and added to pipeline`);
      setSelected(new Set());
    } catch { showToast('Bulk approve failed'); }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(leads.map(l => l.id)));

  const scoreColor  = (s: number) => s >= 80 ? 'text-green-600' : s >= 65 ? 'text-yellow-600' : 'text-gray-500';
  const emailBadge  = (s: string) => s === 'VALID' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700';
  const pendingWhales = leads.filter(l => l.is_whale && l.queue_status === 'pending').length;

  return (
    <div className="space-y-5 pb-10">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white px-4 py-3 rounded-xl shadow-xl text-sm">
          {toast}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Scraper Leads Queue</h1>
            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">PRA INTERNAL</span>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            Review scraped insurance prospects before they enter the main pipeline
            {pendingWhales > 0 && <span className="ml-2 text-yellow-600 font-semibold">🐋 {pendingWhales} Whale{pendingWhales > 1 ? 's' : ''} pending</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/scraper-control')}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            ⚙ Scraper Control
          </button>
          {selected.size > 0 && (
            <button onClick={bulkApprove}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
              ✓ Approve {selected.size} Selected
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Pending Review', value: total,          color: 'text-blue-600' },
          { label: 'Whales Pending', value: pendingWhales,  color: 'text-yellow-600' },
          { label: 'Selected',       value: selected.size,  color: 'text-purple-600' },
          { label: 'Showing',        value: leads.length,   color: 'text-gray-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {(['pending','approved','rejected','all'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors capitalize ${
                filter === f ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}>{f}</button>
          ))}
        </div>
        {filter === 'pending' && leads.length > 0 && (
          <button onClick={selectAll} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
            Select all ({leads.length})
          </button>
        )}
        <button onClick={fetchLeads} className="text-xs text-gray-500 hover:text-gray-700 ml-auto">↻ Refresh</button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <p className="text-gray-400 text-sm">Loading queue...</p>
        </div>
      ) : leads.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <p className="text-gray-500 text-sm">No {filter} leads in queue.</p>
          {filter === 'pending' && (
            <p className="text-xs text-gray-400 mt-1">Run the scraper from the Scraper Control panel to populate.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {leads.map(lead => (
            <div key={lead.id}
              className={`bg-white rounded-xl border-2 p-4 transition-all ${
                selected.has(lead.id)  ? 'border-blue-400 bg-blue-50' :
                lead.is_whale          ? 'border-yellow-300 bg-yellow-50' :
                'border-gray-200'
              }`}>
              <div className="flex items-start gap-3">
                {filter === 'pending' && (
                  <input type="checkbox" checked={selected.has(lead.id)}
                    onChange={() => toggleSelect(lead.id)}
                    className="mt-1 w-4 h-4 rounded flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-sm font-semibold text-gray-900">{lead.company_name}</p>
                    {lead.is_whale && <span className="text-xs font-bold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">🐋 WHALE</span>}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${emailBadge(lead.email_status)}`}>{lead.email_status}</span>
                    <span className={`text-xs font-bold ${scoreColor(lead.lead_score)}`}>Score: {lead.lead_score}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-0.5 text-xs text-gray-500">
                    {lead.state && <span>📍 {lead.state}</span>}
                    {lead.email && <span>✉ {lead.email}</span>}
                    {lead.phone && <span>📞 {lead.phone}</span>}
                    {lead.estimated_premium > 0 && (
                      <span className="font-medium text-green-700">💰 Est. ${lead.estimated_premium.toLocaleString()}/yr</span>
                    )}
                    {lead.policy_types && <span className="col-span-2">🛡 {lead.policy_types}</span>}
                    {lead.industry    && <span className="col-span-2">🏢 {lead.industry}</span>}
                  </div>
                </div>
                {filter === 'pending' && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => approveLead(lead.id)} disabled={processing === lead.id}
                      className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium">
                      {processing === lead.id ? '...' : '✓ Approve'}
                    </button>
                    <button onClick={() => rejectLead(lead.id)} disabled={processing === lead.id}
                      className="px-3 py-1.5 text-xs bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50">
                      ✕
                    </button>
                  </div>
                )}
                {filter === 'approved' && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-lg flex-shrink-0">In Pipeline</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScraperLeadsQueue;
